import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Mail, GraduationCap, Inbox, Eye, MessageSquare, Star, Check, X, Trash2 } from "lucide-react";
import { api } from "@/lib/config";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export default function AdminPage() {
    const [token, setToken] = useState(localStorage.getItem("aba_admin_token") || "");
    const [pwd, setPwd] = useState("");
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [inscriptions, setInscriptions] = useState([]);
    const [comments, setComments] = useState([]);
    const [stats, setStats] = useState(null);

    const isAuth = !!token;

    const login = async (e) => {
        e.preventDefault();
        if (!pwd) return;
        setLoading(true);
        try {
            const res = await api.post("/admin/login", { password: pwd });
            localStorage.setItem("aba_admin_token", res.data.token);
            setToken(res.data.token);
            toast.success("Connecté");
        } catch (err) {
            toast.error("Mot de passe incorrect");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("aba_admin_token");
        setToken("");
    };

    useEffect(() => {
        if (!isAuth) return;
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuth]);

    const loadAll = async () => {
        try {
            const [c, i, s, cm] = await Promise.all([
                api.get("/admin/contacts"),
                api.get("/admin/inscriptions"),
                api.get("/admin/stats"),
                api.get("/admin/comments"),
            ]);
            setContacts(c.data || []);
            setInscriptions(i.data || []);
            setStats(s.data || null);
            setComments(cm.data || []);
        } catch (err) {
            if (err?.response?.status === 401) {
                logout();
                toast.error("Session expirée");
            } else {
                toast.error("Erreur de chargement");
            }
        }
    };

    const updateCommentStatus = async (id, status) => {
        try {
            await api.patch(`/admin/comments/${id}/status`, { status });
            toast.success(status === "approved" ? "Témoignage publié" : status === "rejected" ? "Témoignage rejeté" : "Statut mis à jour");
            await loadAll();
        } catch {
            toast.error("Erreur — action impossible");
        }
    };

    const deleteComment = async (id) => {
        if (!window.confirm("Supprimer définitivement ce témoignage ?")) return;
        try {
            await api.delete(`/admin/comments/${id}`);
            toast.success("Témoignage supprimé");
            await loadAll();
        } catch {
            toast.error("Erreur lors de la suppression");
        }
    };

    if (!isAuth) {
        return (
            <main className="min-h-screen flex items-center justify-center px-6 bg-cream" data-testid="admin-login-page">
                <form
                    onSubmit={login}
                    className="w-full max-w-md bg-card border border-border rounded-sm p-8 md:p-10"
                >
                    <div className="flex items-center gap-2 mb-6">
                        <span className="w-9 h-9 rounded-sm bg-brand text-cream flex items-center justify-center">
                            <Lock size={16} />
                        </span>
                        <span className="label-overline text-stone2">Espace administrateur</span>
                    </div>
                    <h1 className="font-heading text-3xl font-light tracking-tighter text-ink leading-tight">
                        Connexion
                    </h1>
                    <p className="mt-2 text-sm text-stone2">
                        Atelier du Bâti Ancien — back-office
                    </p>
                    <div className="mt-8 space-y-1.5">
                        <Label className="text-xs font-medium uppercase tracking-wider text-ink/80">
                            Mot de passe
                        </Label>
                        <Input
                            data-testid="admin-password"
                            type="password"
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                            placeholder="••••••••"
                            autoFocus
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="mt-6 w-full px-5 py-3 bg-brand hover:bg-brand-hover disabled:opacity-60 text-cream rounded-sm font-medium tracking-tight"
                    >
                        {loading ? "Connexion…" : "Se connecter"}
                    </button>
                    <a
                        href="/"
                        className="block mt-4 text-center text-sm text-stone2 hover:text-brand"
                        data-testid="admin-back-home"
                    >
                        ← Retour au site
                    </a>
                </form>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
            <header className="border-b border-border bg-card">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-sm bg-brand text-cream font-heading text-lg font-semibold flex items-center justify-center">
                            A
                        </span>
                        <div className="flex flex-col leading-tight">
                            <span className="font-heading text-base font-semibold text-ink tracking-tight">
                                Back-office
                            </span>
                            <span className="label-overline text-stone2">Atelier du Bâti Ancien</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            className="text-sm text-stone2 hover:text-brand hidden sm:inline"
                            data-testid="admin-go-home"
                        >
                            Voir le site
                        </a>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-sm hover:border-brand"
                        >
                            <LogOut size={14} /> Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                {/* Stats */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
                    <StatCard
                        icon={Inbox}
                        label="Demandes de contact"
                        value={stats?.contacts_total ?? "—"}
                        sub={`${stats?.contacts_new ?? 0} nouvelles`}
                    />
                    <StatCard
                        icon={GraduationCap}
                        label="Inscriptions formations"
                        value={stats?.inscriptions_total ?? "—"}
                    />
                    <StatCard
                        icon={MessageSquare}
                        label="Témoignages"
                        value={stats?.comments_approved ?? "—"}
                        sub={`${stats?.comments_pending ?? 0} en attente`}
                    />
                    <StatCard
                        icon={Eye}
                        label="Visites du site"
                        value={stats?.visits_total ?? "—"}
                        sub={`${stats?.visits_unique ?? 0} visiteurs uniques`}
                    />
                </section>

                <Tabs defaultValue="contacts">
                    <TabsList data-testid="admin-tabs">
                        <TabsTrigger value="contacts" data-testid="admin-tab-contacts">
                            Contacts ({contacts.length})
                        </TabsTrigger>
                        <TabsTrigger value="inscriptions" data-testid="admin-tab-inscriptions">
                            Inscriptions ({inscriptions.length})
                        </TabsTrigger>
                        <TabsTrigger value="comments" data-testid="admin-tab-comments">
                            Témoignages ({comments.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="contacts" className="mt-6">
                        <DataTable
                            testid="admin-contacts-table"
                            empty="Aucune demande pour l'instant."
                            columns={["Date", "Nom", "Email", "Téléphone", "Sujet", "Service", "Message"]}
                            rows={contacts.map((c) => [
                                fmt(c.created_at),
                                c.name,
                                <a key="m" href={`mailto:${c.email}`} className="text-brand">{c.email}</a>,
                                c.phone || "—",
                                c.subject,
                                c.service ? <Badge variant="secondary" className="font-normal">{c.service}</Badge> : "—",
                                <span key="msg" className="line-clamp-2 max-w-[280px] block">{c.message}</span>,
                            ])}
                        />
                    </TabsContent>

                    <TabsContent value="inscriptions" className="mt-6">
                        <DataTable
                            testid="admin-inscriptions-table"
                            empty="Aucune inscription pour l'instant."
                            columns={["Date", "Nom", "Email", "Téléphone", "Formation", "Profil", "Message"]}
                            rows={inscriptions.map((i) => [
                                fmt(i.created_at),
                                i.full_name,
                                <a key="m" href={`mailto:${i.email}`} className="text-brand">{i.email}</a>,
                                i.phone || "—",
                                <Badge key="f" className="bg-brand/10 text-brand border border-brand/30 font-normal">
                                    {i.formation_title}
                                </Badge>,
                                i.profile || "—",
                                <span key="msg" className="line-clamp-2 max-w-[280px] block">{i.message || "—"}</span>,
                            ])}
                        />
                    </TabsContent>

                    <TabsContent value="comments" className="mt-6">
                        {comments.length === 0 ? (
                            <div className="bg-card border border-border rounded-sm p-12 text-center text-stone2" data-testid="admin-comments-empty">
                                Aucun témoignage soumis pour l'instant.
                            </div>
                        ) : (
                            <div className="grid gap-4" data-testid="admin-comments-list">
                                {comments.map((c) => (
                                    <article
                                        key={c.id}
                                        data-testid={`admin-comment-${c.id}`}
                                        className="bg-card border border-border rounded-sm p-5 md:p-6"
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-heading text-base font-medium text-ink tracking-tight">
                                                        {c.author_name}
                                                    </span>
                                                    {c.location && (
                                                        <span className="text-xs text-stone2">· {c.location}</span>
                                                    )}
                                                    <StatusBadge status={c.status} />
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <Star
                                                            key={i}
                                                            size={12}
                                                            className={i < c.rating ? "fill-brand text-brand" : "text-border"}
                                                            strokeWidth={1.5}
                                                        />
                                                    ))}
                                                    <span className="text-xs text-stone2 ml-2">{fmt(c.created_at)}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {c.status !== "approved" && (
                                                    <button
                                                        onClick={() => updateCommentStatus(c.id, "approved")}
                                                        data-testid={`comment-approve-${c.id}`}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand hover:bg-brand-hover text-cream rounded-sm text-xs font-medium"
                                                    >
                                                        <Check size={13} /> Publier
                                                    </button>
                                                )}
                                                {c.status !== "rejected" && (
                                                    <button
                                                        onClick={() => updateCommentStatus(c.id, "rejected")}
                                                        data-testid={`comment-reject-${c.id}`}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-destructive text-ink rounded-sm text-xs"
                                                    >
                                                        <X size={13} /> Rejeter
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => deleteComment(c.id)}
                                                    data-testid={`comment-delete-${c.id}`}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-destructive text-stone2 hover:text-destructive rounded-sm text-xs"
                                                    aria-label="Supprimer"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-sm text-ink/85 leading-relaxed whitespace-pre-wrap">
                                            {c.message}
                                        </p>
                                        {c.email && (
                                            <p className="text-xs text-stone2 mt-3">
                                                Email privé : <a href={`mailto:${c.email}`} className="text-brand hover:underline">{c.email}</a>
                                            </p>
                                        )}
                                    </article>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </main>
    );
}

function StatCard({ icon: Icon, label, value, sub }) {
    return (
        <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center justify-between">
                <div className="label-overline text-stone2">{label}</div>
                <Icon size={18} className="text-brand" />
            </div>
            <div className="mt-3 font-heading text-3xl font-light tracking-tighter text-ink">
                {value}
            </div>
            {sub && <div className="text-xs text-stone2 mt-1">{sub}</div>}
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending: { label: "En attente", cls: "bg-sand/60 text-ink border-border" },
        approved: { label: "Publié", cls: "bg-brand/15 text-brand border-brand/30" },
        rejected: { label: "Rejeté", cls: "bg-destructive/10 text-destructive border-destructive/30" },
    };
    const s = map[status] || map.pending;
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${s.cls}`}>
            {s.label}
        </span>
    );
}

function DataTable({ columns, rows, empty, testid }) {
    if (!rows.length) {
        return (
            <div className="bg-card border border-border rounded-sm p-12 text-center text-stone2" data-testid={`${testid}-empty`}>
                {empty}
            </div>
        );
    }
    return (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid={testid}>
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((c) => (
                            <TableHead key={c} className="text-xs uppercase tracking-wider text-stone2">{c}</TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={i} data-testid={`${testid}-row-${i}`}>
                            {r.map((cell, j) => (
                                <TableCell key={j} className="text-sm text-ink/90 align-top py-4">
                                    {cell}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
